import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import api from "../interceptors";
import "./index.css";

const PasswordInput = ({
  label,
  value,
  onChange,
  visible,
  onToggle,
  autoComplete,
}) => (
  <label className="profile-field">
    <span>{label}</span>
    <div className="profile-password-wrap">
      <LockKeyhole size={18} />
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required
      />
      <button
        type="button"
        aria-label={visible ? "Hide password" : "Show password"}
        onClick={onToggle}
      >
        {visible ? <EyeOff size={19} /> : <Eye size={19} />}
      </button>
    </div>
  </label>
);

const UserProfile = () => {
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/api/profile");
        setProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const validatePassword = (pwd) => {
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const minLength = pwd.length >= 8;

    if (!minLength) return "Password must be at least 8 characters long.";
    if (!hasUpper) return "Password must contain at least one uppercase letter.";
    if (!hasNumber) return "Password must contain at least one number.";
    if (!hasSpecial)
      return "Password must contain at least one special character.";

    return null;
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }

    setIsUpdating(true);

    try {
      const res = await api.post("/api/update_password", {
        old_password: oldPassword,
        new_password: newPassword,
      });

      setPasswordSuccess(res.data.message || "Password updated successfully.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => setPasswordSuccess(""), 2200);
    } catch (err) {
      setPasswordError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update password.",
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const firstLetter = profile?.username
    ? profile.username.charAt(0).toUpperCase()
    : "?";

  return (
    <main className="profile-shell">
      {loading ? (
        <div className="profile-loading">
          <LoaderCircle size={38} />
        </div>
      ) : !profile ? (
        <div className="profile-empty">
          <p>Failed to load profile.</p>
        </div>
      ) : (
        <>
          <section className="profile-hero">
            <div className="profile-avatar">
              {profile?.picture ? (
                <img
                  src={profile.picture}
                  referrerPolicy="no-referrer"
                  alt="User"
                />
              ) : (
                <span>{firstLetter}</span>
              )}
            </div>

            <div>
              <p>User profile</p>
              <h1>{profile.username}</h1>
            </div>

            <span className="profile-security-chip">
              <ShieldCheck size={17} />
              Secure account
            </span>
          </section>

          <section className="profile-grid">
            <article className="profile-card">
              <div className="profile-card-heading">
                <span>
                  <UserRound size={22} />
                </span>
                <div>
                  <p>Identity</p>
                  <h2>Account details</h2>
                </div>
              </div>

              <div className="profile-detail-list">
                <div>
                  <span>
                    <UserRound size={18} />
                    Username
                  </span>
                  <strong>{profile.username}</strong>
                </div>
                <div>
                  <span>
                    <Mail size={18} />
                    Email
                  </span>
                  <strong>{profile.email}</strong>
                </div>
                <div>
                  <span>
                    <Phone size={18} />
                    Mobile Number
                  </span>
                  <strong>{profile.mobile_number || "Not provided"}</strong>
                </div>
              </div>
            </article>

            <article className="profile-card profile-security-card">
              {isUpdating && (
                <div className="profile-updating">
                  <LoaderCircle size={34} />
                </div>
              )}

              <div className="profile-card-heading">
                <span>
                  <LockKeyhole size={22} />
                </span>
                <div>
                  <p>Security</p>
                  <h2>Update password</h2>
                </div>
              </div>

              {(passwordError || passwordSuccess) && (
                <div
                  className={`profile-message ${
                    passwordSuccess ? "is-success" : "is-error"
                  }`}
                >
                  {passwordSuccess && <CheckCircle2 size={18} />}
                  {passwordSuccess || passwordError}
                </div>
              )}

              <form onSubmit={handleUpdatePassword} className="profile-form">
                <PasswordInput
                  label="Old Password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  visible={showOld}
                  onToggle={() => setShowOld((value) => !value)}
                  autoComplete="current-password"
                />

                <PasswordInput
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  visible={showNew}
                  onToggle={() => setShowNew((value) => !value)}
                  autoComplete="new-password"
                />

                <PasswordInput
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  visible={showConfirm}
                  onToggle={() => setShowConfirm((value) => !value)}
                  autoComplete="new-password"
                />

                <button
                  type="submit"
                  disabled={isUpdating || Boolean(passwordSuccess)}
                  className="profile-submit"
                >
                  {isUpdating ? (
                    <>
                      <LoaderCircle size={18} className="profile-spin" />
                      Updating
                    </>
                  ) : passwordSuccess ? (
                    <>
                      <CheckCircle2 size={18} />
                      Updated
                    </>
                  ) : (
                    "Update password"
                  )}
                </button>
              </form>
            </article>
          </section>
        </>
      )}
    </main>
  );
};

export default UserProfile;
